export interface OptionPage {
  data: unknown[]
  total?: number
  page?: number
  limit?: number
  totalPages?: number
}

/** Collect every matching option through the existing, permission-scoped list API.
 * Transport batches are invisible to the selector and never cap its results.
 * A failed/inconsistent page rejects the whole load instead of presenting a partial list.
 */
export async function fetchAllOptions<T extends OptionPage>(
  endpoint: string,
  get: (url: string) => Promise<T>
): Promise<T> {
  const [pathname, query = ''] = endpoint.split('?')
  const params = new URLSearchParams(query)
  const batchSize = 100
  params.set('limit', String(batchSize))
  const data: unknown[] = []
  const seenPages = new Set<string>()
  for (let page = 1; ; page++) {
    params.set('page', String(page))
    const result = await get(`${pathname}?${params}`)
    if (!Array.isArray(result.data)) throw new Error('候选数据格式错误，请重试')
    const signature = JSON.stringify(result.data)
    if (result.data.length && seenPages.has(signature))
      throw new Error('候选数据未完整加载，请重试')
    seenPages.add(signature)
    data.push(...result.data)
    const total = result.total
    if (
      typeof total === 'number'
        ? data.length >= total
        : result.data.length < (result.limit || batchSize)
    ) {
      return {
        ...result,
        data,
        total: data.length,
        page: 1,
        limit: data.length,
        totalPages: 1,
      } as T
    }
    if (!result.data.length) throw new Error('候选数据未完整加载，请重试')
  }
}

/** A type selection may fill/refresh a default, but must preserve a custom name. */
export function completeDeviceName(
  current: string,
  previousTypeName: string | undefined,
  nextTypeName: string | undefined
) {
  return !current.trim() || current === previousTypeName
    ? nextTypeName || current
    : current
}
